import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  InputGroup,
  InputGroupText,
  Table,
  Badge,
} from "reactstrap";
import api from "../../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InspectionSection from "./InspectionSection";
import "../di-theme.css";
import { MafiaPageHeader, TableSkeleton, EmptyState } from "../../Mafia/_shared";
import { partRevisionText } from "../../Mafia/mafiaPrintHeading";

/**
 * Replaces the legacy WinForms `Simselect.vb` screen.
 *
 * Legacy behavior preserved:
 * - Loads all parts (legacy: MafiaDB.GetAllParts; modern: GET /Inspection/parts-all)
 * - Search box: Enter jumps to first part whose PartNumber starts with the
 *   typed value, scrolls it into view and re-selects the search text so the
 *   user can keep typing — matches Simselect.vb txtFilter.KeyDown logic.
 * - Row click selects; selection enables "Open Inspection".
 *
 * Bug fix: prior version had a broken `api` import (`../../config/api`
 * does not exist) which would have thrown on first load. Restored the
 * correct path `../../../api` matching the convention in
 * InspectionSection.js and sibling files.
 *
 * UI consistency: rewrites raw <input>/<button>/<table> as Reactstrap
 * Card/CardBody/CardHeader/Button/Input/Table to match every other Mafia
 * and DI page.
 *
 * MAFIA Aux Simulator round — this SAME component is now also the MAFIA Aux
 * "Simulator" tile (legacy: the MAFIA Auxiliary Toolbox "Simulator" button
 * launched the Digital Inspection Simselect.vb screen — see
 * SWhite-Digital-Inspection/Simselect.vb: GetAllParts grid → pick part →
 * open Inspect read-only with jobnum "12345" / quantity 500 and saves
 * disabled). Instead of duplicating the screen under src/pages/Mafia, the
 * Mafia MenuPage renders this component with three OPTIONAL props; all
 * default so the existing /di/simulator route behavior is byte-identical:
 *  - embedded:   true  → skip the page-content/container-fluid wrappers
 *                (the Mafia MenuPage already provides them).
 *  - onBack:     callback → shows a "Back to Menu" button in the header
 *                (matches every other Mafia sub-page).
 *  - breadcrumb: overrides the default DI breadcrumb so the trail reads
 *                Dashboard > MAFIA Aux > Simulator when opened from Mafia.
 */
const SimSelect = ({ embedded = false, onBack = null, breadcrumb = null }) => {
  const [parts, setParts] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const [launchInspect, setLaunchInspect] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [loadingParts, setLoadingParts] = useState(false);

  const tableBodyRef = useRef(null);
  const loadSeqRef = useRef(0);
  const searchInputRef = useRef(null);

  // Mimic VB: fixed simulator values unless you want to expose UI for them.
  // These match the legacy Simselect.vb hardcoded job context.
  const jobNum = "12345";
  const quantity = 500;
  const waveNumber = "1";
  const seq = 0;
  const lotNum = 0;
  const originalpartFromSelection = true;

  const [enteredBy, setEnteredBy] = useState("Unknown");

  useEffect(() => {
    try {
      const authData = sessionStorage.getItem("authUser");
      if (authData) {
        const parsed = JSON.parse(authData);
        setEnteredBy(parsed?.name || "Unknown");
      }
    } catch {}
  }, []);

  // ── PERFORMANCE FIX (slow load) ──
  // The endpoint previously returned EVERY revision of EVERY part in one
  // response. Now loads in pages of 300 (slim 3-column payload) with
  // server-side PartNumber prefix search; "Load more" appends the next
  // page. The legacy Enter-to-jump becomes a server prefix filter — same
  // gesture, instant result even on huge tables.
  const PAGE_SIZE = 300;
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const activeQueryRef = useRef("");

  const fetchParts = useCallback(
    async ({ query = activeQueryRef.current, offset = 0, append = false } = {}) => {
      const seqNo = ++loadSeqRef.current;
      if (append) setLoadingMore(true);
      else setLoadingParts(true);
      try {
        const res = await api.get("/Inspection/parts-all", {
          params: { q: query || "", limit: PAGE_SIZE, offset },
        });
        if (seqNo !== loadSeqRef.current) return;
        const page = Array.isArray(res.data) ? res.data : [];
        activeQueryRef.current = query || "";
        setHasMore(page.length === PAGE_SIZE);
        setParts((prev) => {
          const next = append ? [...prev, ...page] : page;
          setSelectedIndex((sel) => (sel >= 0 && sel < next.length ? sel : -1));
          return next;
        });
      } catch {
        if (seqNo !== loadSeqRef.current) return;
        toast.error("Failed to load parts list.");
      } finally {
        if (seqNo === loadSeqRef.current) {
          setLoadingParts(false);
          setLoadingMore(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    fetchParts({ query: "", offset: 0 });
  }, [fetchParts]);

  const loadMore = useCallback(() => {
    fetchParts({ offset: parts.length, append: true });
  }, [fetchParts, parts.length]);

  const selectedRow = useMemo(() => {
    if (selectedIndex < 0 || selectedIndex >= parts.length) return null;
    return parts[selectedIndex];
  }, [selectedIndex, parts]);

  const canOpen = !!selectedRow;

  const handleFilterChange = useCallback((e) => {
    setFilterText(e.target.value);
  }, []);

  const onSearchKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter") return;
      // Server-side prefix search (legacy Enter-to-jump, done where the
      // data lives). Empty input restores the unfiltered first page.
      fetchParts({ query: filterText.trim(), offset: 0 });
      setSelectedIndex(-1);
    },
    [filterText, fetchParts]
  );

  const handleOpenInspection = useCallback(() => {
    if (!selectedRow) {
      toast.info("Please select a part");
      return;
    }

    setSelectedPart({
      partNum: selectedRow.PartNumber,
      revMajor: Number(selectedRow.PrintRevision) || 0,
      revMinor: Number(selectedRow.MafiaRevision) || 0,
    });
    setLaunchInspect(true);
  }, [selectedRow]);

  const handleRowClick = useCallback((idx) => {
    setSelectedIndex(idx);
  }, []);

  if (launchInspect && selectedPart) {
    return (
      <div className={embedded ? "di-module" : "page-content di-module"}>
        {/* Same invisible-toast fix as the list view below. */}
        <ToastContainer />
        <div className={embedded ? "" : "container-fluid"}>
          <div className="mb-3">
            <button
              type="button"
              className="di-btn di-btn--ghost"
              onClick={() => setLaunchInspect(false)}
            >
              <i className="ri-arrow-left-line" />
              Back to parts
            </button>
          </div>
          <InspectionSection
        jobNum={jobNum}
        waveNumber={waveNumber}
        revMajor={selectedPart.revMajor}
        revMinor={selectedPart.revMinor}
        seq={seq}
        lotNum={lotNum}
        enteredBy={enteredBy}
        partNum={selectedPart.partNum}
        quantity={quantity}
        originalpart={
          originalpartFromSelection ? selectedPart.partNum : undefined
        }
        // Batch 3 / Round 15 — legacy Simselect parity: the preview is
        // strictly read-only (legacy disabled Panel1 + comments and the
        // engineer flag skipped all writes). Every entry box, the
        // comment, Save and the close actions are disabled.
        readOnly
      />
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "di-module" : "page-content di-module"}>
      {/* UAT Test 11 companion fix: this page (and the read-only
          InspectionSection it hosts) also calls toast.* with no container
          mounted anywhere in the app, so its messages were invisible. */}
      <ToastContainer />
      <div className={embedded ? "" : "container-fluid"}>
      <MafiaPageHeader
        icon="ri-cpu-line"
        eyebrow="Simulator"
        title="Select Part"
        subtitle="Choose a part to launch the simulator inspection workspace."
        tone="teal"
        actions={
          <>
            <Badge
              color="light"
              className="fw-medium px-2 py-1"
              style={{
                background: "rgba(255,255,255,0.18)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
              }}
            >
              <i className="ri-stack-line align-bottom me-1" />
              {parts.length}
              {hasMore ? "+" : ""} parts
            </Badge>
            {onBack ? (
              <Button
                color="light"
                onClick={onBack}
                style={{
                  background: "rgba(255,255,255,0.92)",
                  border: "none",
                  fontWeight: 500,
                }}
              >
                <i className="ri-arrow-left-line align-bottom me-1" />
                Back to Menu
              </Button>
            ) : null}
          </>
        }
        breadcrumb={
          breadcrumb || [
            { label: "Dashboards", to: "/" },
            { label: "Digital Inspection", to: "/di" },
            { label: "Simulator" },
          ]
        }
      />

      <Card className="border-0 shadow-sm">
        <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <InputGroup style={{ maxWidth: 420 }}>
              <InputGroupText className="bg-light border-end-0">
                <i className="ri-search-line" />
              </InputGroupText>
              <Input
                innerRef={searchInputRef}
                type="text"
                className="border-start-0"
                placeholder="Type part number and press Enter…"
                value={filterText}
                onChange={handleFilterChange}
                onKeyDown={onSearchKeyDown}
                autoFocus
              />
            </InputGroup>

            <Button
              color="primary"
              onClick={handleOpenInspection}
              disabled={!canOpen}
              title={!canOpen ? "Select a part first" : "Open Inspection"}
            >
              <i className="ri-play-line align-bottom me-1" />
              Open Inspection
            </Button>
          </div>
        </CardHeader>

        <CardBody className="p-4">
          <div
            className="border rounded"
            style={{ maxHeight: 420, overflow: "auto" }}
          >
            <Table className="table-sm table-hover mb-0 align-middle">
              <thead
                className="table-light"
              style={{ position: "sticky", top: 0, zIndex: 1 }}
            >
              <tr>
                <th style={{ width: "26%" }}>Part Number</th>
                {/* 2026-10-01 (tester): the same words and two digits as the MAFIA printout
                    (DB column is still Rev / PrintRevision alias — unchanged). Only verified
                    and released revisions are listed, with the person who verified them. */}
                <th style={{ width: "12%" }}>Part Revision</th>
                <th style={{ width: "12%" }}>MAFIA Revision</th>
                {/* Go-live parity (2026-09-24): legacy Simselect columns. */}
                <th style={{ width: "16%" }}>Created By</th>
                <th style={{ width: "16%" }}>Verified By</th>
                <th style={{ width: "9%" }}>Verified</th>
                <th style={{ width: "9%" }}>Released</th>
              </tr>
            </thead>
              <tbody ref={tableBodyRef}>
                {loadingParts ? (
                  <TableSkeleton rows={6} columns={7} />
                ) : parts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-0">
                      <EmptyState
                        icon="ri-cpu-line"
                        title="No parts available"
                        message="No verified and released part revisions were found. Only verified and released MAFIA tables are listed here."
                        tone="secondary"
                      />
                    </td>
                  </tr>
                ) : (
                  parts.map((p, idx) => {
                    const isSelected = idx === selectedIndex;
                    return (
                      <tr
                        key={`${p.PartNumber}-${idx}`}
                        onClick={() => handleRowClick(idx)}
                        onDoubleClick={() => {
                          handleRowClick(idx);
                          // double-click = select + open (one-gesture launch)
                          setSelectedPart({
                            partNum: p.PartNumber,
                            revMajor: Number(p.PrintRevision) || 0,
                            revMinor: Number(p.MafiaRevision) || 0,
                          });
                          setLaunchInspect(true);
                        }}
                        className={isSelected ? "table-active" : ""}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="fw-semibold text-dark">{p.PartNumber}</td>
                        <td>{partRevisionText(p.PrintRevision)}</td>
                        <td>{p.MafiaRevision}</td>
                        <td>{p.CreatedBy ?? ""}</td>
                        <td>{p.VerifiedBy ?? ""}</td>
                        <td>{p.Verified ? "Yes" : "No"}</td>
                        <td>{p.Released ? "Yes" : "No"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
          </Table>
        </div>

        {/* paged loading: 300 rows per page, server-side prefix search */}
        <div className="d-flex align-items-center justify-content-between p-2 border-top">
          <span className="text-muted small">
            Showing {parts.length} part revision{parts.length === 1 ? "" : "s"}
            {hasMore ? " — more available" : ""}
          </span>
          {hasMore && (
            <button
              type="button"
              className="di-btn di-btn--ghost"
              onClick={loadMore}
              disabled={loadingMore}
            >
              <i className="ri-arrow-down-line" />
              {loadingMore ? "Loading..." : "Load more"}
            </button>
          )}
        </div>

          {selectedRow && (
            <div className="mt-3 small text-muted">
              Selected:{" "}
              <strong className="text-dark">{selectedRow.PartNumber}</strong>{" "}
              (Part Rev: {partRevisionText(selectedRow.PrintRevision)}, MAFIA Rev:{" "}
              {selectedRow.MafiaRevision})
            </div>
          )}
        </CardBody>
      </Card>
      </div>
    </div>
  );
};

export default React.memo(SimSelect);
